import Dropdown from '@proton/components/components/dropdown/Dropdown'
import DropdownButton from '@proton/components/components/dropdown/DropdownButton'
import DropdownMenu from '@proton/components/components/dropdown/DropdownMenu'
import DropdownMenuButton from '@proton/components/components/dropdown/DropdownMenuButton'
import { DropdownSizeUnit } from '@proton/components/components/dropdown/utils'
import SimpleDropdown from '@proton/components/components/dropdown/SimpleDropdown'
import Toggle from '@proton/components/components/toggle/Toggle'
import useAppTitle from '@proton/components/hooks/useAppTitle'
import useAuthentication from '@proton/components/hooks/useAuthentication'
import { usePopperAnchor } from '@proton/atoms/Popper/usePopperAnchor'
import { useConfig } from '@proton/app-context/useConfig'
import { useNotifications } from '@proton/app-context/useNotifications'
import { getAppVersion } from '@proton/components/helpers/appVersion'
import { useCallback, useEffect, useRef, useState } from 'react'

import { mergeRegister } from '@lexical/utils'
import { c } from 'ttag'

import { CircleLoader } from '@proton/atoms/CircleLoader/CircleLoader'
import { IcArrowDownToSquare } from '@proton/icons/icons/IcArrowDownToSquare'
import { IcArrowsCross } from '@proton/icons/icons/IcArrowsCross'
import { IcBrandProtonDocs } from '@proton/icons/icons/IcBrandProtonDocs'
import { IcBrandProtonDrive } from '@proton/icons/icons/IcBrandProtonDrive'
import { IcBrandProtonSheets } from '@proton/icons/icons/IcBrandProtonSheets'
import { IcChevronRightFilled } from '@proton/icons/icons/IcChevronRightFilled'
import { IcClockRotateLeft } from '@proton/icons/icons/IcClockRotateLeft'
import { IcCogWheel } from '@proton/icons/icons/IcCogWheel'
import { IcFileArrowInUp } from '@proton/icons/icons/IcFileArrowInUp'
import { IcInfoCircle } from '@proton/icons/icons/IcInfoCircle'
import { IcLockCheck } from '@proton/icons/icons/IcLockCheck'
import { IcPencil } from '@proton/icons/icons/IcPencil'
import { IcPrinter } from '@proton/icons/icons/IcPrinter'
import { IcSquares } from '@proton/icons/icons/IcSquares'
import { IcTrash } from '@proton/icons/icons/IcTrash'
import type {
  AuthenticatedDocControllerInterface,
  DocumentState,
  PublicDocumentState,
  EditorControllerInterface,
  RenameControllerInterface,
} from '@proton/docs-core'
import { isDocumentState, PostApplicationError } from '@proton/docs-core'
import type {
  FileMenuAction,
  SheetImportData,
  NodeMeta,
  DocTrashState,
  DocumentType,
  DocumentAction,
} from '@proton/docs-shared'
import { FileMenuActionEvent, isWordCountSupported } from '@proton/docs-shared'
import { isPrivateNodeMeta } from '@proton/drive-store'
import { getAppHref } from '@proton/shared/lib/apps/helper'
import { APPS, APPS_CONFIGURATION, DRIVE_APP_NAME } from '@proton/shared/lib/constants'
import { isDefaultDocumentName } from '@proton/shared/lib/docs/utils/isDefaultDocumentName'
import { getStaticURL } from '@proton/shared/lib/helpers/url'
import { useApplication } from '~/utils/application-context'
import { AutoGrowingInput } from './AutoGrowingInput'
import { useHistoryViewerModal } from '../HistoryViewerModal/HistoryViewerModal'
import { TrashedDocumentModal } from './TrashedDocumentModal'
import { useWordCount } from '../../../WordCount'
import { useExportToPDFModal } from './ExportToPDFModal'
import { useDocsContext } from '../../../context'
import { WordCountIcon } from '../icons'
import { useSheetImportModal } from './SheetImportModal'
import { downloadLogsAsJSON } from '~/utils/downloadLogs'
import { useEvent } from '~/utils/misc'
import {
  useIsSheetsEnabled,
  useIsDownloadLogsAllowed,
  useMoveModalDriveSdkEnabled,
  useRenameWithSDK,
  useIsTableOfContentsEnabled,
  useTrashWithSDK,
} from '~/utils/flags'
import { useDebugMode } from '~/utils/debug-mode-context'
import * as Ariakit from '@ariakit/react'
import clsx from '@proton/utils/clsx'
import * as UI from '@proton/docs-shared/components/ui/ui'
import { textToClipboard } from '@proton/shared/lib/helpers/browser'
import { VersionNumber } from '@proton/docs-shared/components/ui/VersionNumber'
import { versionCookieAtLoad } from '@proton/components/helpers/versionCookie'
import { useMoveItemsModal } from '@proton/drive/public/moveItemsModal'
import { generateNodeUid } from '@proton/drive'
import { IcListBullets } from '@proton/icons/icons/IcListBullets'
import type { UserModel } from '@proton/shared/lib/interfaces'

export type DocumentTitleDropdownProps = {
  authenticatedController: AuthenticatedDocControllerInterface | undefined
  renameController: RenameControllerInterface | undefined
  editorController: EditorControllerInterface
  documentState: DocumentState | PublicDocumentState
  actionMode?: DocumentAction['mode']
  documentType: DocumentType
}

const SupportsFieldSizing = CSS.supports('field-sizing: content')

export function DocumentTitleDropdown({
  authenticatedController,
  renameController,
  editorController,
  documentState,
  actionMode,
  documentType,
}: DocumentTitleDropdownProps) {
  const application = useApplication()
  const isPublicMode = application.isPublicMode
  const { publicContext, privateContext } = useDocsContext()
  const user = privateContext?.user || publicContext?.user
  const { getLocalID } = useAuthentication()
  const wordCount = useWordCount()
  const { toggleDebugMode } = useDebugMode()
  const isDownloadLogsAllowed = useIsDownloadLogsAllowed()
  const { APP_VERSION } = useConfig()
  const appVersion = getAppVersion(APP_VERSION)
  const { createNotification } = useNotifications()
  const moveModalDriveSdkEnabled = useMoveModalDriveSdkEnabled()
  const renameWithSDK = useRenameWithSDK()
  const trashWithSDK = useTrashWithSDK()
  const isSheetsEnabled = useIsSheetsEnabled()
  const isTableOfContentsFeatureEnabled = useIsTableOfContentsEnabled()

  const [pdfModal, openPdfModal] = useExportToPDFModal()
  const [historyModal, showHistoryModal] = useHistoryViewerModal()
  const [sheetImportModal, showSheetImportModal] = useSheetImportModal()
  const { moveItemsModal, showMoveItemsModal } = useMoveItemsModal()

  const [tableOfContentsVisible, setTableOfContentsVisible] = useState(isTableOfContentsFeatureEnabled)
  const [title, setTitle] = useState<string | undefined>(documentState.getProperty('documentName'))
  const [isDuplicating, setIsDuplicating] = useState<boolean>(false)
  const [trashState, setTrashState] = useState<DocTrashState | undefined>(
    documentState.getProperty('documentTrashState'),
  )
  const [isMakingNewDocument, setIsMakingNewDocument] = useState<boolean>(false)
  const [isMakingNewSheetDocument, setIsMakingNewSheetDocument] = useState<boolean>(false)
  const [showVersionNumber, setShowVersionNumber] = useState(false)
  const [showDebugToggle, setShowDebugToggle] = useState(false)
  const [isRenaming, setIsRenaming] = useState(false)
  const [renameInputValue, setRenameInputValue] = useState(title)
  useEffect(() => {
    setRenameInputValue(title)
  }, [title])
  const renameInputRef = useRef<HTMLInputElement>(null)
  const shouldSelectDefaultSpreadsheetTitleOnMouseUpRef = useRef(false)

  const isSpreadsheet = documentType === 'sheet'

  useAppTitle(title, isSpreadsheet ? APPS_CONFIGURATION[APPS.PROTONSHEETS].name : undefined)

  useEffect(() => {
    // When the user holds down the shift key, show the version number. When they release, hide it.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.shiftKey) {
        setShowVersionNumber(true)
        if (event.ctrlKey) {
          setShowDebugToggle((prev) => !prev)
        }
      }
    }

    const handleKeyUp = (event: KeyboardEvent) => {
      if (!event.shiftKey) {
        setShowVersionNumber(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [])

  const confirmRename = useEvent(() => {
    renameInputRef.current?.scrollTo(0, 0)

    if (!renameController) {
      throw new Error('Cannot rename document without rename controller')
    }

    const oldName = title
    const newName = renameInputValue?.trim()
    if (newName) {
      setIsRenaming(false)

      if (oldName !== newName) {
        void renameController.renameDocument(newName, renameWithSDK).then((result) => {
          if (result.isFailed()) {
            PostApplicationError(application.eventBus, { translatedError: result.getTranslatedError() })
            setTitle(oldName)
          } else if (renameWithSDK && privateContext) {
            // Public rename uses legacy - already has the notification
            const successNotificationText = c('Notification').jt`"${newName}" renamed successfully`
            createNotification({
              text: <span className="text-pre-wrap">{successNotificationText}</span>,
            })
          }
        })
      }
      setTitle(newName)
    } else {
      setIsRenaming(false)
    }
  })

  const onDuplicate = useCallback(async () => {
    if (!authenticatedController) {
      throw new Error('Attempting to duplicate document in a public context')
    }

    setIsDuplicating(true)

    try {
      const editorState = await editorController?.exportData('yjs')
      if (editorState) {
        await authenticatedController.duplicateDocument(editorState)
      }
    } finally {
      setIsDuplicating(false)
    }
  }, [authenticatedController, editorController])

  const printAsPDF = useCallback(() => {
    editorController.printAsPDF().catch(console.error)
  }, [editorController])

  const onExportPDF = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.preventDefault()
      event.stopPropagation()

      void openPdfModal({ onClose: printAsPDF })
    },
    [openPdfModal, printAsPDF],
  )

  useEffect(() => {
    return mergeRegister(
      documentState.subscribeToProperty('documentName', (title) => {
        setTitle(title)
      }),

      documentState.subscribeToProperty('documentTrashState', (trashState) => {
        setTrashState(trashState)
      }),
    )
  }, [documentState, authenticatedController])

  const onNewDocument = useCallback(
    // We default to creating the same type of document as the current one, but allow creating the other type too
    async (docType: DocumentType = documentType) => {
      const setIsLoading = docType === 'doc' ? setIsMakingNewDocument : setIsMakingNewSheetDocument
      setIsLoading(true)
      try {
        await authenticatedController?.createNewDocument(docType)
      } finally {
        setIsLoading(false)
      }
    },
    [authenticatedController, documentType],
  )

  useEffect(() => {
    if (actionMode === 'history') {
      if (!authenticatedController) {
        throw new Error('Attempting to view version history in a public context')
      }

      showHistoryModal({
        versionHistory: authenticatedController.getVersionHistory(),
        editorController,
        docController: authenticatedController,
        documentType,
        documentState,
      })
    }
  }, [authenticatedController, actionMode, showHistoryModal, editorController, documentType, documentState])

  const { anchorRef, isOpen, toggle, close } = usePopperAnchor<HTMLButtonElement>()
  const focusInputOnMount = useCallback((input: HTMLInputElement | null) => {
    if (input) {
      input.focus()
      input.select()
    }
  }, [])

  useEffect(() => {
    if (!isOpen) {
      setShowDebugToggle(false)
    }
  }, [isOpen])

  const openProtonDrive = useCallback(
    (to = '/', target = '_blank') => {
      window.open(getAppHref(to, APPS.PROTONDRIVE, getLocalID()), target)
    },
    [getLocalID],
  )

  const openRecentSpreadsheets = useCallback(() => {
    window.open(getAppHref('/', APPS.PROTONSHEETS, getLocalID()), '_blank')
  }, [getLocalID])

  const openDriveFolderForDocument = useCallback(async () => {
    const node = documentState.getProperty('decryptedNode')
    const nodeMeta = documentState.getProperty('entitlements').nodeMeta
    let to: string | undefined
    if (!!privateContext && isPrivateNodeMeta(nodeMeta)) {
      const { compat } = privateContext
      // Drive is share-based and not volume-based so we need
      // to get and use the shareId (and not volumeId)
      const shareId = await compat.getShareId(nodeMeta)
      if (node.parentNodeId) {
        to = `/${shareId}/folder/${node.parentNodeId}`
      }
    }
    openProtonDrive(to)
  }, [documentState, openProtonDrive, privateContext])

  const handleSheetImportData = useCallback(
    (data: SheetImportData) => {
      void editorController.importDataIntoSheet(data)
    },
    [editorController],
  )

  const handleDownloadLogs = useCallback(() => {
    void downloadLogsAsJSON(editorController, documentType)
  }, [editorController, documentType])

  const openSheetImportModal = useCallback(() => {
    showSheetImportModal({
      handleImport: handleSheetImportData,
    })
  }, [handleSheetImportData, showSheetImportModal])

  const openMoveToFolderModal = useCallback(() => {
    if (moveModalDriveSdkEnabled && !isPublicMode) {
      const { volumeId, linkId } = documentState.getProperty('entitlements').nodeMeta as NodeMeta
      showMoveItemsModal({ nodeUids: [generateNodeUid(volumeId, linkId)] })
    } else {
      authenticatedController?.openMoveToFolderModal()
    }
  }, [authenticatedController, documentState, isPublicMode, moveModalDriveSdkEnabled, showMoveItemsModal])

  const trashDocument = useCallback(async () => {
    if (!authenticatedController) {
      throw new Error('Attempting to trash document in a public context')
    }

    try {
      await authenticatedController.trashDocument(trashWithSDK)
    } finally {
      close()
    }
  }, [authenticatedController, close, trashWithSDK])

  const showVersionHistory = useCallback(() => {
    if (!authenticatedController) {
      throw new Error('Attempting to view version history in a public context')
    }
    showHistoryModal({
      versionHistory: authenticatedController.getVersionHistory(),
      editorController,
      docController: authenticatedController,
      documentType,
      documentState,
    })
  }, [authenticatedController, editorController, documentType, showHistoryModal, documentState])

  useEffect(() => {
    function syncTableOfContentsStateToEditor() {
      void editorController.setTableOfContentsVisible(tableOfContentsVisible)
    }
    if (documentState.getProperty('editorReady')) {
      syncTableOfContentsStateToEditor()
    }
    return documentState.subscribeToProperty('editorReady', (ready) => {
      if (ready) {
        syncTableOfContentsStateToEditor()
      }
    })
  }, [documentState, editorController, tableOfContentsVisible])

  function handleTableOfContentsToggle() {
    const nextTableOfContentsVisible = !tableOfContentsVisible
    setTableOfContentsVisible(nextTableOfContentsVisible)
    void editorController.setTableOfContentsVisible(nextTableOfContentsVisible)
  }

  const openHelp = useCallback(() => {
    window.open(getStaticURL(isSpreadsheet ? '/support/drive/sheets' : '/support/drive'), '_blank')
  }, [isSpreadsheet])

  useEffect(() => {
    return application.eventBus.addEventCallback(async (data: FileMenuAction) => {
      switch (data.type) {
        case 'new-spreadsheet':
          return onNewDocument('sheet')
        case 'new-document':
          return onNewDocument('doc')
        case 'import':
          openSheetImportModal()
          return
        case 'make-a-copy':
          return onDuplicate()
        case 'move-to-folder':
          openMoveToFolderModal()
          break
        case 'see-version-history':
          showVersionHistory()
          break
        case 'move-to-trash':
          return trashDocument()
        case 'print':
          printAsPDF()
          break
        case 'help':
          openHelp()
          break
        case 'open-proton-drive':
          openProtonDrive()
          break
        case 'download':
          void editorController.exportAndDownload(data.format)
          break
        case 'view-recent-spreadsheets':
          openRecentSpreadsheets()
          break
        case 'toggle-debug-mode':
          toggleDebugMode()
          break
      }
    }, FileMenuActionEvent)
  }, [
    application.eventBus,
    onDuplicate,
    onNewDocument,
    openMoveToFolderModal,
    openSheetImportModal,
    showVersionHistory,
    trashDocument,
    printAsPDF,
    openHelp,
    openProtonDrive,
    editorController,
    openRecentSpreadsheets,
    toggleDebugMode,
  ])

  if (isSpreadsheet) {
    const input = (
      <Ariakit.TooltipProvider>
        <Ariakit.TooltipAnchor
          render={
            <input
              type="text"
              className={clsx(
                'text-ellipsis rounded-[4px] px-1 py-1.5 [border:1px_solid_transparent] focus-visible:outline-none enabled:focus:border-[#6D4AFF] enabled:hover:[&:not(:focus)]:bg-[#C2C1C033]',
                SupportsFieldSizing
                  ? 'ml-2 mr-1.5 min-w-4 [field-sizing:content]'
                  : 'w-auto max-w-full [grid-area:1_/_2]',
              )}
              value={renameInputValue}
              onChange={(e) => setRenameInputValue(e.target.value)}
              onKeyDown={(e) => {
                const { key, currentTarget } = e
                if (key === 'Escape') {
                  setRenameInputValue(title)
                  editorController.focusSpreadsheet()
                  e.preventDefault()
                  e.stopPropagation()
                } else if (key === 'Enter') {
                  currentTarget.blur()
                  editorController.focusSpreadsheet()
                }
              }}
              onFocus={(e) => {
                if (!isDefaultDocumentName(e.currentTarget.value, 'sheet')) {
                  return
                }
                // Defer so the click's mouseup doesn't clobber the selection by placing the caret.
                const input = e.currentTarget
                shouldSelectDefaultSpreadsheetTitleOnMouseUpRef.current = true
                setTimeout(() => {
                  if (document.activeElement === input) {
                    input.select()
                  }
                })
              }}
              onMouseUp={(e) => {
                if (
                  !shouldSelectDefaultSpreadsheetTitleOnMouseUpRef.current ||
                  !isDefaultDocumentName(e.currentTarget.value, 'sheet')
                ) {
                  return
                }
                shouldSelectDefaultSpreadsheetTitleOnMouseUpRef.current = false
                e.preventDefault()
                e.currentTarget.select()
              }}
              onBlur={confirmRename}
              ref={renameInputRef}
              data-testid="sheet-name-input"
              size={SupportsFieldSizing ? undefined : 1} // required to make sure the input will shrink to fit the content when `field-sizing` is not supported
              disabled={!canRename(documentState, user)}
            />
          }
        ></Ariakit.TooltipAnchor>
        <UI.Tooltip>{c('Action').t`Rename`}</UI.Tooltip>
      </Ariakit.TooltipProvider>
    )

    return (
      <>
        {SupportsFieldSizing ? (
          input
        ) : (
          <div className="relative ml-2 mr-1.5 inline-grid items-center">
            {input}
            <div
              className="w-auto select-none overflow-hidden whitespace-pre border px-1 py-1.5 [grid-area:1_/_2] [visibility:hidden]"
              aria-hidden="true"
            >
              {renameInputValue}
            </div>
          </div>
        )}
        {historyModal}
        {pdfModal}
        {sheetImportModal}
        {moveItemsModal}
        {authenticatedController && isDocumentState(documentState) && (
          <TrashedDocumentModal
            documentTitle={title || ''}
            onOpenProtonDrive={() => openProtonDrive('/', '_self')}
            controller={authenticatedController}
            documentState={documentState}
          />
        )}
      </>
    )
  }

  if (isRenaming) {
    return (
      <div className="flex items-center px-1.5 pl-1">
        <AutoGrowingInput
          inputClassName="px-1 py-1.5"
          ref={focusInputOnMount}
          value={renameInputValue}
          onValue={setRenameInputValue}
          onKeyDown={({ key }) => {
            if (key === 'Escape') {
              setIsRenaming(false)
              setRenameInputValue(title)
            } else if (key === 'Enter') {
              confirmRename()
            }
          }}
          onBlur={confirmRename}
        />
      </div>
    )
  }

  if (!title) {
    return
  }

  return (
    <>
      <DropdownButton
        ref={anchorRef}
        isOpen={isOpen}
        onClick={toggle}
        hasCaret
        shape="ghost"
        size="small"
        className="w-fit whitespace-nowrap px-1.5 py-1.5"
        data-testid="document-name-dropdown"
      >
        <span className="text-ellipsis text-left head-480-749:!max-w-[215px]">{title}</span>
      </DropdownButton>
      <Dropdown
        isOpen={isOpen}
        anchorRef={anchorRef}
        onClose={close}
        size={{
          width: DropdownSizeUnit.Dynamic,
          height: DropdownSizeUnit.Dynamic,
          maxHeight: DropdownSizeUnit.Viewport,
        }}
        originalPlacement="bottom-start"
        className="py-0"
        contentProps={{
          className: 'after:!h-0',
        }}
      >
        <DropdownMenu>
          {canRename(documentState, user) && (
            <DropdownMenuButton
              className="flex items-center text-left"
              onClick={() => setIsRenaming((renaming) => !renaming)}
              data-testid="dropdown-rename"
            >
              <IcPencil className="color-weak mr-2" />
              {c('Action').t`Rename document`}
            </DropdownMenuButton>
          )}

          {!isPublicMode && (
            <DropdownMenuButton
              disabled={isMakingNewDocument}
              className="flex items-center text-left"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                void onNewDocument()
              }}
              data-testid="dropdown-new-document"
            >
              <IcBrandProtonDocs className="color-weak mr-2" />
              {c('Action').t`New document`}
              {isMakingNewDocument && <CircleLoader size="small" className="ml-auto" />}
            </DropdownMenuButton>
          )}

          {!isPublicMode && isSheetsEnabled ? (
            <DropdownMenuButton
              disabled={isMakingNewSheetDocument}
              className="flex items-center text-left"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                void onNewDocument('sheet')
              }}
            >
              <IcBrandProtonSheets className="color-weak mr-2" />
              {c('sheets_2025:Action').t`New spreadsheet`}
              {isMakingNewSheetDocument ? <CircleLoader size="small" className="ml-auto" /> : null}
            </DropdownMenuButton>
          ) : null}

          {isSpreadsheet && documentState.getProperty('userRole').canEdit() && (
            <DropdownMenuButton
              className="flex items-center text-left"
              data-testid="dropdown-sheet-import"
              onClick={openSheetImportModal}
            >
              <IcFileArrowInUp className="color-weak mr-2" />
              {c('Action').t`Import`}
            </DropdownMenuButton>
          )}

          {!isPublicMode && (
            <DropdownMenuButton
              disabled={isDuplicating}
              className="flex items-center text-left"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                void onDuplicate()
              }}
              data-testid="dropdown-duplicate"
            >
              <IcSquares className="color-weak mr-2" />
              {c('Action').t`Make a copy`}
              {isDuplicating && <CircleLoader size="small" className="ml-auto" />}
            </DropdownMenuButton>
          )}

          {!isPublicMode && authenticatedController && (
            <DropdownMenuButton
              className="flex items-center text-left"
              data-testid="dropdown-move-to-folder"
              onClick={openMoveToFolderModal}
            >
              <IcArrowsCross className="color-weak mr-2" />
              {c('Action').t`Move to folder`}
            </DropdownMenuButton>
          )}

          {!isPublicMode && (
            <DropdownMenuButton
              className="flex items-center text-left"
              onClick={showVersionHistory}
              data-testid="dropdown-versioning"
            >
              <IcClockRotateLeft className="color-weak mr-2" />
              {c('Action').t`See version history`}
            </DropdownMenuButton>
          )}

          {isWordCountSupported && !isSpreadsheet && (
            <SimpleDropdown
              as={DropdownMenuButton}
              className="flex items-center text-left"
              data-testid="dropdown-word-count"
              content={
                <>
                  <WordCountIcon className="color-weak mr-2 h-4 w-4" />
                  {c('Action').t`Word count`}
                  <span className="ml-auto text-[--text-hint]">{wordCount.document?.wordCount}</span>
                  <IcChevronRightFilled className="ml-2" />
                </>
              }
              hasCaret={false}
              onClick={(event: MouseEvent) => {
                event.preventDefault()
                event.stopPropagation()
              }}
              originalPlacement="right-start"
              contentProps={{
                offset: 0,
              }}
            >
              <DropdownMenu>
                <DropdownMenuButton
                  className="flex items-center gap-1 text-left"
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                  }}
                  data-testid="word-count-label"
                >
                  <span className="text-bold">{wordCount.document?.wordCount} </span> {c('Info').t`words`}
                </DropdownMenuButton>
                <DropdownMenuButton
                  className="flex items-center gap-1 text-left"
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                  }}
                  data-testid="character-count-label"
                >
                  <span className="text-bold">{wordCount.document?.characterCount}</span> {c('Info').t`characters`}
                </DropdownMenuButton>
                <DropdownMenuButton
                  className="flex items-center gap-1 text-left"
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                  }}
                  data-testid="non-whitespace-character-count-label"
                >
                  <span className="text-bold">{wordCount.document?.nonWhitespaceCharacterCount}</span>
                  {c('Info').t`characters without spaces`}
                </DropdownMenuButton>
                <DropdownMenuButton
                  className="flex items-center gap-1 text-left"
                  onClick={(event) => {
                    wordCount.setEnabled((value) => !value)
                    event.preventDefault()
                    event.stopPropagation()
                  }}
                  data-testid="non-whitespace-character-count-label"
                >
                  {c('Info').t`Display word count`}
                  <Toggle data-testid="floating-word-count-toggle" className="ml-auto" checked={wordCount.enabled} />
                </DropdownMenuButton>
              </DropdownMenu>
            </SimpleDropdown>
          )}

          {!isSpreadsheet && isTableOfContentsFeatureEnabled && (
            <DropdownMenuButton className="flex items-center text-left" onClick={handleTableOfContentsToggle}>
              <IcListBullets className="color-weak mr-2" />
              {tableOfContentsVisible ? c('Info').t`Hide table of contents` : c('Info').t`Show table of contents`}
            </DropdownMenuButton>
          )}

          {documentState.getProperty('userRole').canTrash() && (
            <DropdownMenuButton
              disabled={trashState === 'trashing' || trashState === 'trashed'}
              data-testid="dropdown-trash"
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                void trashDocument()
              }}
              className="flex items-center text-left"
            >
              <IcTrash className="color-weak mr-2" />
              {c('Action').t`Move to trash`}
              {trashState === 'trashing' && <CircleLoader size="small" className="ml-auto" />}
            </DropdownMenuButton>
          )}

          <hr className="my-1 min-h-px" />

          <DropdownMenuButton className="flex items-center text-left" onClick={printAsPDF} data-testid="dropdown-print">
            <IcPrinter className="color-weak mr-2" />
            {c('Action').t`Print`}
          </DropdownMenuButton>

          <SimpleDropdown
            as={DropdownMenuButton}
            className="flex items-center text-left"
            data-testid="dropdown-download"
            content={
              <>
                <IcArrowDownToSquare className="color-weak mr-2" />
                {c('Action').t`Download`}
                <IcChevronRightFilled className="ml-auto" />
              </>
            }
            hasCaret={false}
            onClick={(event: MouseEvent) => {
              event.preventDefault()
              event.stopPropagation()
            }}
            originalPlacement="right-start"
            contentProps={{
              offset: 0,
            }}
          >
            {isSpreadsheet && (
              <DropdownMenu>
                <DropdownMenuButton
                  className="flex items-center text-left"
                  onClick={() => {
                    void editorController.exportAndDownload('xlsx')
                  }}
                  data-testid="download-xlsx"
                >
                  {c('sheets_2025:Action').t`Microsoft Excel (.xlsx)`}
                </DropdownMenuButton>
                <DropdownMenuButton
                  className="flex items-center text-left"
                  onClick={() => {
                    void editorController.exportAndDownload('csv')
                  }}
                  data-testid="download-csv"
                >
                  {c('sheets_2025:Action').t`Comma Separated Values (.csv)`}
                </DropdownMenuButton>
                <DropdownMenuButton
                  className="flex items-center text-left"
                  onClick={() => {
                    void editorController.exportAndDownload('tsv')
                  }}
                  data-testid="download-tsv"
                >
                  {c('sheets_2025:Action').t`Tab Separated Values (.tsv)`}
                </DropdownMenuButton>
              </DropdownMenu>
            )}
            {!isSpreadsheet && (
              <DropdownMenu>
                <DropdownMenuButton
                  className="flex items-center text-left"
                  onClick={() => {
                    void editorController.exportAndDownload('docx')
                  }}
                  data-testid="download-docx"
                >
                  {c('Action').t`Microsoft Word (.docx)`}
                </DropdownMenuButton>
                <DropdownMenuButton
                  className="flex items-center text-left"
                  onClick={() => {
                    void editorController.exportAndDownload('html')
                  }}
                  data-testid="download-html"
                >
                  {c('Action').t`Web page (.html)`}
                </DropdownMenuButton>
                <DropdownMenuButton
                  className="flex items-center text-left"
                  onClick={() => {
                    void editorController.exportAndDownload('txt')
                  }}
                  data-testid="download-txt"
                >
                  {c('Action').t`Plain Text (.txt)`}
                </DropdownMenuButton>
                <DropdownMenuButton
                  className="flex items-center text-left"
                  onClick={() => {
                    void editorController.exportAndDownload('md')
                  }}
                  data-testid="download-md"
                >
                  {c('Action').t`Markdown (.md)`}
                </DropdownMenuButton>
                <DropdownMenuButton
                  className="flex items-center text-left"
                  onClick={onExportPDF}
                  data-testid="download-pdf"
                >
                  {c('Action').t`PDF (.pdf)`}
                </DropdownMenuButton>
              </DropdownMenu>
            )}
          </SimpleDropdown>

          {!isSpreadsheet && (
            <DropdownMenuButton
              className="flex items-center text-left"
              onClick={async () => {
                const result = await editorController.getCurrentSelection('md')
                if (result) {
                  textToClipboard(result)
                  createNotification({
                    type: 'success',
                    text: c('Info').t`Copied to clipboard`,
                  })
                }
              }}
              data-testid="dropdown-copy-as-md"
            >
              <IcSquares className="color-weak mr-2" />
              {c('Action').t`Copy as markdown`}
            </DropdownMenuButton>
          )}

          <hr className="my-1 min-h-px" />

          <DropdownMenuButton className="flex items-center text-left" onClick={openHelp} data-testid="dropdown-help">
            <IcInfoCircle className="color-weak mr-2" />
            {c('Action').t`Help`}
            {showVersionNumber && (
              <VersionNumber
                className="ml-auto text-[--text-hint]"
                version={appVersion}
                environment={versionCookieAtLoad}
              />
            )}
          </DropdownMenuButton>

          <DropdownMenuButton
            className="flex items-center text-left"
            onClick={openDriveFolderForDocument}
            data-testid="dropdown-open-drive"
          >
            <IcBrandProtonDrive className="color-weak mr-2" />
            {c('Action').t`Open ${DRIVE_APP_NAME}`}
          </DropdownMenuButton>

          {isDownloadLogsAllowed && (
            <DropdownMenuButton
              className="flex items-center text-left"
              onClick={handleDownloadLogs}
              data-testid="dropdown-download-logs"
            >
              <IcArrowDownToSquare className="color-weak mr-2" />
              {c('Action').t`Download logs`}
            </DropdownMenuButton>
          )}

          {showDebugToggle && (
            <DropdownMenuButton
              className="flex items-center text-left"
              onClick={toggleDebugMode}
              data-testid="dropdown-download-logs"
            >
              <IcCogWheel className="color-weak mr-2" />
              {c('Action').t`Toggle debug mode`}
            </DropdownMenuButton>
          )}

          <hr className="mb-0 mt-1 min-h-px" />

          <div
            className="color-weak px-4 py-2 text-sm"
            style={{
              background: 'var(--primary-minor-2)',
            }}
            data-testid="e2e-info"
          >
            <IcLockCheck className="color-weak mr-2 align-middle" />
            <span className="align-middle">{c('Info').t`End-to-end encrypted.`} </span>
            <a
              data-connection-popover
              className="mr-2 inline-block align-middle underline hover:underline"
              href="https://proton.me/security/end-to-end-encryption"
              target="_blank"
              rel="noreferrer"
            >
              {c('Info').t`Learn more`}
            </a>
          </div>
        </DropdownMenu>
      </Dropdown>

      {historyModal}
      {pdfModal}
      {sheetImportModal}
      {moveItemsModal}
      {authenticatedController && isDocumentState(documentState) && (
        <TrashedDocumentModal
          documentTitle={title}
          onOpenProtonDrive={() => openProtonDrive('/', '_self')}
          controller={authenticatedController}
          documentState={documentState}
        />
      )}
    </>
  )
}

// We should also check if document created in a public shared folder was created by the current user
// Unfortunately, we don't have that information neither from legacy nor SDK path (needs to be investigated how to fetch it)
function canRename(documentState: DocumentState | PublicDocumentState, user: UserModel | undefined) {
  // Theoretically you should be able to rename document anymously, but currently BE throws "This value should not be blank." error
  const hasPermission = !!user && documentState.getProperty('userRole').canRename()

  if (documentState.getProperty('userRole').isPublicEditor()) {
    return hasPermission && publicDocumentCanBeRenamed(documentState)
  }
  return hasPermission
}

// Public documents can be renamed in first hour after creation
function publicDocumentCanBeRenamed(documentState: DocumentState | PublicDocumentState) {
  return Date.now() / 1000 - documentState.getProperty('documentMeta').createTime <= 59 * 60 // 59 minutes
}
